/**
 * The guided retry's state changes as pure functions (pedagogy spec §5.3.1, §5.3.6): what a
 * withheld first try, the retry and "Jag kör fast" each write into a `QuestionResult`, and the
 * body the retry POST sends. `SessionPage` calls them; they are unit-tested without the DOM.
 *
 * `QuestionResult.correct`, `.score` and `.maxScore` always stay the first try's values (§12 Q12:
 * the session score counts first tries); what the retry did is in `retried`.
 */
import type { PublicQuestion } from "@shared/types";
import type {
  AnswerBody,
  AnswerResponse,
  PendingAnswerResponse,
  RevealedQuestion,
  TerminalAnswerResponse,
} from "./types";
import type { QuestionResult, ResultPart } from "./session";

/** What the student sent with the try this response answers. */
export interface SentTry {
  /** Numeric: every part as typed. */
  numericRaw?: Record<string, string>;
  /** Single-select MC: the pick. */
  mcSelected?: string[];
  /** MC: the trimmed justification. */
  justification?: string;
}

/**
 * True for a withheld first try. A response without the flag (a server from before the retry)
 * is terminal, so an old build keeps working.
 */
export function isPending(res: AnswerResponse): res is PendingAnswerResponse {
  return res.retryAvailable === true;
}

/** A graded answer with its solution: a right first try, an item without a retry, or the retry. */
export function terminalPatch(res: TerminalAnswerResponse, sent: SentTry): Partial<QuestionResult> {
  return {
    answered: true,
    terminal: true,
    correct: res.correct,
    score: res.score,
    maxScore: res.maxScore,
    revealed: res.revealed,
    attemptId: res.attemptId,
    ...(res.parts.length > 0 ? { parts: res.parts } : {}),
    ...(res.mcDiagnosis ? { mcDiagnosis: res.mcDiagnosis } : {}),
    ...(res.retestDue ? { retestDue: res.retestDue } : {}),
    ...(sent.mcSelected ? { selectedOptionIds: sent.mcSelected } : {}),
    ...(sent.justification ? { justification: sent.justification } : {}),
    ...followUpPatch(res),
  };
}

/**
 * What a terminal response adds for the follow-ups (step 2.6): a contrast follow-up's prediction
 * verdict, and the contrast twins an ordinary item offers. A pending response carries neither.
 */
export function followUpPatch(res: TerminalAnswerResponse): Partial<QuestionResult> {
  return {
    ...(res.prediction ? { predictionResult: res.prediction } : {}),
    ...(res.followUps && res.followUps.contrasts.length > 0
      ? { followUps: res.followUps.contrasts }
      : {}),
  };
}

/**
 * A withheld first try: not terminal, no solution, the first try kept for the retry panel. The
 * automatic hint joins `hintsRevealed` so the ladder shows it and the retry's `hintsUsed` counts it.
 */
export function pendingPatch(
  res: PendingAnswerResponse,
  sent: SentTry,
  existing: QuestionResult,
): Partial<QuestionResult> {
  const hint = res.autoHint;
  // The server opens the hint at the `hintsUsed` we sent; anything else is not ours to append.
  const takesHint = hint !== undefined && existing.hintsRevealed.length === hint.index;
  return {
    answered: true,
    terminal: false,
    retryPending: true,
    correct: res.correct,
    score: res.score,
    maxScore: res.maxScore,
    attemptId: res.attemptId,
    ...(takesHint ? { hintsRevealed: [...existing.hintsRevealed, hint.hint] } : {}),
    ...(res.retestDue ? { retestDue: res.retestDue } : {}),
    ...(sent.mcSelected ? { selectedOptionIds: sent.mcSelected } : {}),
    ...(sent.justification ? { justification: sent.justification } : {}),
    firstTry: {
      attemptId: res.attemptId,
      correct: res.correct,
      score: res.score,
      maxScore: res.maxScore,
      ...(res.parts.length > 0 ? { parts: res.parts } : {}),
      ...(sent.numericRaw ? { numericRaw: sent.numericRaw } : {}),
      ...(sent.mcSelected ? { mcSelected: sent.mcSelected } : {}),
      ...(res.mcDiagnosis ? { mcDiagnosis: res.mcDiagnosis } : {}),
      ...(takesHint ? { autoHint: true } : {}),
    },
  };
}

/** The retry was graded: terminal, the solution folded, the first try's numbers untouched. */
export function retriedPatch(
  res: TerminalAnswerResponse,
  sent: SentTry,
  existing: QuestionResult,
): Partial<QuestionResult> {
  const retestDue = res.retestDue ?? existing.retestDue;
  return {
    answered: true,
    terminal: true,
    retryPending: false,
    retried: { correct: res.correct, score: res.score, maxScore: res.maxScore },
    revealed: res.revealed,
    attemptId: res.attemptId,
    ...(res.parts.length > 0 ? { parts: res.parts } : {}),
    ...(res.mcDiagnosis ? { mcDiagnosis: res.mcDiagnosis } : {}),
    ...(retestDue ? { retestDue } : {}),
    ...(sent.mcSelected ? { selectedOptionIds: sent.mcSelected } : {}),
    ...(sent.justification ? { justification: sent.justification } : {}),
    ...followUpPatch(res),
  };
}

/**
 * "Jag kör fast": `/reveal` answered, nothing was graded. The solution opens, and each part's
 * `expected` comes from the revealed answer so "Rätt svar:" renders (the pending response had
 * none); the student's own text stands in for the parsed value the server withheld.
 */
export function skippedPatch(
  existing: QuestionResult,
  revealed: RevealedQuestion,
): Partial<QuestionResult> {
  const pendingParts = existing.firstTry?.parts ?? [];
  const parts: ResultPart[] =
    revealed.type === "numeric"
      ? pendingParts.flatMap((part) => {
          const answer = revealed.parts.find((r) => r.id === part.id);
          if (!answer) return [];
          const typed = existing.firstTry?.numericRaw?.[part.id];
          return [
            {
              id: part.id,
              correct: part.correct,
              parsedValue: null,
              expected: answer.answer,
              unit: answer.unit || part.unit,
              ...(typed !== undefined ? { typed } : {}),
              ...(part.diagnosis ? { diagnosis: part.diagnosis } : {}),
            },
          ];
        })
      : [];
  return {
    terminal: true,
    retryPending: false,
    solutionOpen: true,
    revealed,
    ...(parts.length > 0 ? { parts } : {}),
    ...(existing.firstTry?.mcDiagnosis ? { mcDiagnosis: existing.firstTry.mcDiagnosis } : {}),
  };
}

/** The ids of the parts the first try got right: they stay locked and are resent as typed. */
export function keptPartIds(result: QuestionResult): string[] {
  return (result.firstTry?.parts ?? []).filter((part) => part.correct).map((part) => part.id);
}

/** The new attempt the student has drafted for the retry. */
export interface RetryDraft {
  numeric: Record<string, string>;
  mc: string[];
  justification: string;
}

/**
 * The retry's request body: the whole item again, the kept parts as typed the first time, the
 * rest as re-entered. `hintsUsed` is the running total (the automatic hint included). The retry
 * never carries a rating, a plan pick or `segmented`.
 */
export function retryBody(
  question: PublicQuestion,
  result: QuestionResult,
  draft: RetryDraft,
  hintsUsed: number,
): AnswerBody | null {
  const firstTry = result.firstTry;
  if (!firstTry) return null;
  if (question.type === "numeric") {
    const kept = new Set(keptPartIds(result));
    const numeric = Object.fromEntries(
      question.parts.map((part) => [
        part.id,
        kept.has(part.id) ? (firstTry.numericRaw?.[part.id] ?? "") : (draft.numeric[part.id] ?? ""),
      ]),
    );
    return {
      questionId: question.id,
      answer: { numeric, hintsUsed },
      retryOf: firstTry.attemptId,
    };
  }
  if (question.type === "mc") {
    const justification = draft.justification.trim();
    return {
      questionId: question.id,
      answer: { mc: draft.mc, ...(justification ? { justification } : {}), hintsUsed },
      retryOf: firstTry.attemptId,
    };
  }
  return null;
}
